import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Button, ButtonVariant } from '../../../common/Button'
import { FormControl } from '../../../common/FormControl'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { translateRole } from '../../../../helpers/society'
import { BoardRole } from '../../../../types/board-roles'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { societyConfirmActivationSchema } from '../../../../forms/schemas/society_confirm_activation'
import { HorizontalFormSection } from '../../../common/HorizontalFormSection'
import { useAuth } from '../../../../hooks/useAuth'
import { createUserRole } from '../../../../api/societies'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'

interface FindSocietyConfirmActivationFormProps {
  society: ISocietiesModel
}

export const FindSocietyConfirmActivationForm = ({
  society,
}: FindSocietyConfirmActivationFormProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const navigate = useNavigate()
  const { userId } = useAuth()
  const boardRoleOptions = Object.values(BoardRole).map((val) => ({
    value: val as string,
    label: translateRole(val) as string | '',
  }))

  const getDefaultValues = (): InferType<
    typeof societyConfirmActivationSchema
  > => {
    return {
      role: BoardRole.PRESIDENT,
      boardMembers: [],
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyConfirmActivationSchema),
    defaultValues: getDefaultValues(),
  })

  const showErrorMessage = (message: string): void => {
    setToastNotification(ToastType.DANGER, message)
  }

  const showSuccessMessage = (message: string): void => {
    setToastNotification(ToastType.SUCCESS, message)
  }

  const navigateToActivatedSocietyConfirmation = (): void => {
    navigate(reverseUrl('activated-society'), { replace: true })
  }

  const { mutate: mutateCreateUserRole, isPending: isCreatingUserRole } =
    useMutation({
      mutationFn: async (
        data: InferType<typeof societyConfirmActivationSchema>
      ) => {
        const response = await createUserRole({
          body: [
            {
              societyId: society._id,
              userId,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              boardRole: data.role,
            },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ...(data.boardMembers
              ? data.boardMembers.map((_boardMember) => ({
                  societyId: society._id,
                  userEmail: _boardMember.email as string,
                  boardRole: BoardRole.MEMBER,
                  unitRole: 'member',
                }))
              : []),
          ],
          sendNotifications: true,
        })

        const status = response.data.data
        if (!status) throw new Error('failed to create user role')
        return { status, data }
      },
      onSuccess: (response) => {
        const { data } = response
        showSuccessMessage(
          data.boardMembers && data.boardMembers.length > 0
            ? translate(
                'findSocietyConfirmActivationForm.flashMessage.invitesSentSuccess'
              )
            : translate('flashMessage.changesSaved')
        )
        navigateToActivatedSocietyConfirmation()
      },
      onError: (error) => {
        showErrorMessage(translate('flashMessage.somethingWentWrongError'))
        captureException(error)
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyConfirmActivationSchema>
  ): Promise<void> => {
    mutateCreateUserRole(data)
  }

  const onError = (): void => {
    showErrorMessage(translate('flashMessage.somethingWentWrongError'))
  }

  const loading = isCreatingUserRole

  return (
    <form
      className="flex w-full flex-col space-y-4 md:space-y-10"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <HorizontalFormSection
        title={translate('findSocietyConfirmActivationForm.roleTitle')}
      >
        <Controller
          control={control}
          name="role"
          render={({ field: { value, name, onChange } }) => (
            <FormControl
              error={errors.role && getErrorMessage(errors.role)}
              name={name}
            >
              <SelectDropdown
                options={boardRoleOptions}
                value={boardRoleOptions.find(
                  (option) => option.value === value
                )}
                onChange={(option) => onChange(option?.value)}
                error={!!errors.role}
              />
            </FormControl>
          )}
        />
      </HorizontalFormSection>
      <div className="flex w-full flex-col justify-end md:w-4/6 md:flex-row">
        <Button
          wrapperClassName="w-full md:w-1/6"
          className="w-full"
          variant={ButtonVariant.PRIMARY}
          type="submit"
          label={translate('common.button.save')}
          loading={loading}
          disabled={!isValid || loading}
        />
      </div>
    </form>
  )
}
